import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/presentation/components/common/Button';
import { Input } from '@/presentation/components/common/Input';
import { ModalShell } from '@/presentation/components/common/ModalShell/ModalShell';
import { useAuth } from '@/presentation/contexts/AuthContext';
import { useToast } from '@/presentation/contexts/ToastContext';
import { httpClient } from '@/infrastructure/http/client/httpClient';
import { UserMapper } from '@/infrastructure/repositories/mappers/UserMapper';
import type { UserDTO } from '@/application/dtos/auth/AuthDTO';

export type ChangePasswordMode = 'forced' | 'profile';

type ChangePasswordModalProps = {
  isOpen: boolean;
  mode: ChangePasswordMode;
  onClose?: () => void;
};

type ChangePasswordResponse = {
  token: string;
  user: UserDTO;
};

export const ChangePasswordModal: React.FC<ChangePasswordModalProps> = ({
  isOpen,
  mode,
  onClose,
}) => {
  const { t } = useTranslation();
  const { applySession } = useAuth();
  const { success } = useToast();
  const forced = mode === 'forced';

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const resetForm = () => {
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setTwoFactorCode('');
    setError(null);
  };

  const handleClose = () => {
    if (forced) return;
    resetForm();
    onClose?.();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (newPassword.length < 8) {
      setError(t('auth.passwordMinLength'));
      return;
    }
    if (newPassword !== confirmPassword) {
      setError(t('auth.passwordMismatch'));
      return;
    }
    if (!forced) {
      if (!currentPassword) {
        setError(t('auth.currentPasswordRequired'));
        return;
      }
      const cleanCode = twoFactorCode.trim().replace(/\s/g, '');
      if (!/^\d{6}$/.test(cleanCode)) {
        setError(t('auth.twoFactorRequired'));
        return;
      }
    }

    setIsSaving(true);
    try {
      const res = await httpClient.post<ChangePasswordResponse>('/auth/change-password', {
        newPassword,
        ...(forced
          ? {}
          : {
              currentPassword,
              twoFactorCode: twoFactorCode.trim().replace(/\s/g, ''),
            }),
      });
      applySession(res.data.token, UserMapper.toDomain(res.data.user));
      success(t('auth.passwordChanged'));
      resetForm();
      onClose?.();
    } catch (err) {
      const message =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined;
      setError(message || t('auth.changePasswordError'));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <ModalShell
      isOpen={isOpen}
      onClose={handleClose}
      title={forced ? t('auth.changePasswordForcedTitle') : t('auth.changePassword')}
      subtitle={forced ? t('auth.changePasswordForcedHint') : t('auth.changePasswordProfileHint')}
      size="sm"
      showCloseButton={!forced}
      closeOnOverlay={!forced}
      closeOnEscape={!forced}
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {!forced ? (
          <Input
            label={t('auth.currentPassword')}
            type="password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            autoComplete="current-password"
            required
          />
        ) : null}
        <Input
          label={t('auth.newPassword')}
          type="password"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          autoComplete="new-password"
          required
        />
        <Input
          label={t('auth.confirmPassword')}
          type="password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          autoComplete="new-password"
          required
        />
        {!forced ? (
          <Input
            label={t('auth.twoFactorCode')}
            type="text"
            value={twoFactorCode}
            onChange={(e) => setTwoFactorCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            placeholder="000000"
            maxLength={6}
            inputMode="numeric"
            autoComplete="one-time-code"
            required
          />
        ) : null}
        {error ? <p className="text-sm text-red-600 dark:text-red-400">{error}</p> : null}
        <div className="flex justify-end gap-2 pt-1">
          {!forced ? (
            <Button type="button" variant="ghost" size="sm" onClick={handleClose}>
              {t('common.cancel')}
            </Button>
          ) : null}
          <Button type="submit" variant="primary" size="sm" isLoading={isSaving}>
            {t('auth.savePassword')}
          </Button>
        </div>
      </form>
    </ModalShell>
  );
};
